import { useState, useEffect, useCallback, useRef } from 'react';
/**
 * Hook to manage menu state with browser history integration.
 * When a menu opens, it pushes a history state so the back button can close it.
 * When the menu is closed by other means (clicking outside, navigation), the history is cleaned up.
 *
 * @param menuId - Unique identifier for this menu (e.g., 'mobile-menu', 'user-menu')
 * @returns [isOpen, setIsOpen] - State and setter similar to useState
 */
export function useMenuHistory(menuId) {
    const [isOpen, setIsOpenInternal] = useState(false);
    // Track whether we pushed a history state for this menu
    const historyPushedRef = useRef(false);
    // Track if we're currently handling a popstate event
    const handlingPopstateRef = useRef(false);
    // Handle popstate (back/forward button)
    useEffect(() => {
        const handlePopstate = (event) => {
            const state = event.state;
            // If we have history pushed and the current state doesn't have our menu open
            if (historyPushedRef.current && state?.menuOpen !== menuId) {
                handlingPopstateRef.current = true;
                setIsOpenInternal(false);
                historyPushedRef.current = false;
                handlingPopstateRef.current = false;
            }
        };
        window.addEventListener('popstate', handlePopstate);
        return () => window.removeEventListener('popstate', handlePopstate);
    }, [menuId]);
    // Setter that manages history
    const setIsOpen = useCallback((open, options) => {
        if (open && !isOpen) {
            // Opening the menu - push history state
            const state = { menuOpen: menuId };
            window.history.pushState(state, '', window.location.href);
            historyPushedRef.current = true;
            setIsOpenInternal(true);
        }
        else if (!open && isOpen) {
            // Closing the menu
            if (historyPushedRef.current && !handlingPopstateRef.current && !options?.skipHistoryBack) {
                // We pushed a history state and aren't handling a popstate event
                // Go back to remove our history entry
                window.history.back();
            }
            historyPushedRef.current = false;
            setIsOpenInternal(false);
        }
    }, [isOpen, menuId]);
    return [isOpen, setIsOpen];
}
